// content/portraits.ts — the立ち絵 pool. Cosmetic content data ONLY: never on the wire
// (PROTOCOL_VERSION stays 1), never touches the engine. A立ち絵 is the JOIN KEY of the
// central loop (flow.md l.41): 門番 = 福引景品 = 段位戦CPUの顔 are the SAME portrait id.
// MVP ships PLACEHOLDER art (flow.md l.509「決」); note/booth art is後差し via the swappable
// `art` field — replacing the asset never changes the id, so saves/collections survive.

/** Which tier a portrait belongs to. */
export type PortraitTier = 'gatekeeper' | 'generic';

/** 段位帯 — coarse rank band a portrait is themed for (drives gacha山 grouping & CPU-face band). */
export type DanBand = 'kyu' | 'jundan' | 'jou' | 'ryuu' | 'ou' | 'ten';

/** How a portrait's art is sourced. MVP = 'placeholder'; later 'note' / 'booth' swap IN PLACE. */
export type PortraitArtSource = 'placeholder' | 'note' | 'booth';

export interface PortraitArt {
  readonly source: PortraitArtSource;
  /** Asset ref. For 'placeholder' a stable synthetic key (e.g. 'placeholder/gk-jundo');
   *  swapped to a real URL/path when note/booth art lands. id is independent of this. */
  readonly ref: string;
}

export interface Portrait {
  /** Stable unique id = collection key + gacha山 key + CPU-face key. NEVER reuse/renumber. */
  readonly id: string;
  readonly tier: PortraitTier;
  readonly band: DanBand;
  readonly name: { readonly ja: string };
  /** For gatekeepers: the段位 transition they guard (flow.md l.80). null for generics. */
  readonly transition: string | null;
  /** Display-ready art, swappable後差し. */
  readonly art: PortraitArt;
}

// ---------------------------------------------------------------------------
// Tier 1 — the 5 gatekeepers (flow.md l.80–87): one per 名称変更 transition.
// These are the 必中・記念枠 in the 福引 gacha山 — the player is guaranteed to collect
// each gatekeeper exactly once at their corresponding段位 transition. Their ids are
// human-readable and stable: NEVER renumber or reuse.
// ---------------------------------------------------------------------------
const GATEKEEPERS: readonly Portrait[] = [
  {
    id: 'gk-jundo',
    tier: 'gatekeeper',
    band: 'jundan',
    name: { ja: '雀道の門番' },
    transition: '雀道',
    art: { source: 'placeholder', ref: 'placeholder/gk-jundo' },
  },
  {
    id: 'gk-shou',
    tier: 'gatekeeper',
    band: 'jou',
    name: { ja: '雀匠の門番' },
    transition: '雀匠',
    art: { source: 'placeholder', ref: 'placeholder/gk-shou' },
  },
  {
    id: 'gk-ryuu',
    tier: 'gatekeeper',
    band: 'ryuu',
    name: { ja: '雀龍の門番' },
    transition: '雀龍',
    art: { source: 'placeholder', ref: 'placeholder/gk-ryuu' },
  },
  {
    id: 'gk-ou',
    tier: 'gatekeeper',
    band: 'ou',
    name: { ja: '雀王の門番' },
    transition: '雀王',
    art: { source: 'placeholder', ref: 'placeholder/gk-ou' },
  },
  {
    id: 'gk-ten',
    tier: 'gatekeeper',
    band: 'ten',
    name: { ja: '天雀の門番' },
    transition: '天雀',
    art: { source: 'placeholder', ref: 'placeholder/gk-ten' },
  },
];

// ---------------------------------------------------------------------------
// Tier 2 — generic placeholders (flow.md l.509「決」: "汎用立ち絵N体, 段位帯×数体").
//
// Sizing rationale (so 福引「約2.6体/ラダー」 yields a long-tail collection goal):
//   - A full ladder crossing all 5 名称変更 transitions awards ~5 招福札.
//   - At 2.6 portraits/ladder the player pulls ~2.6 generics per full climb.
//   - The pool must be large enough that collecting spans many ladders, not one session.
//   - GENERIC_PER_BAND = 5 × 6 段位帯 = 30 total generics.
//   - Combined with 5 gatekeepers → 35 total portraits.
//   - A single 福引山 holds 12 牌; 30 >> 12 guarantees collecting spans multiple ladders.
// ---------------------------------------------------------------------------
export const GENERIC_PER_BAND = 5; // 5 generics per band × 6 bands = 30 generics total

const BANDS: readonly DanBand[] = ['kyu', 'jundan', 'jou', 'ryuu', 'ou', 'ten'];

// Japanese display label per band (used in generic portrait names).
const BAND_JA: Record<DanBand, string> = {
  kyu: '雀士',
  jundan: '雀道',
  jou: '雀匠',
  ryuu: '雀龍',
  ou: '雀王',
  ten: '天雀',
};

function buildGenerics(): readonly Portrait[] {
  const out: Portrait[] = [];
  for (const band of BANDS) {
    for (let n = 1; n <= GENERIC_PER_BAND; n++) {
      const id = `gen-${band}-${n}`;
      out.push({
        id,
        tier: 'generic',
        band,
        name: { ja: `${BAND_JA[band]}の挑戦者${n}` },
        transition: null,
        art: { source: 'placeholder', ref: `placeholder/${id}` },
      });
    }
  }
  return out;
}

const GENERICS: readonly Portrait[] = buildGenerics();

// ---------------------------------------------------------------------------
// PORTRAITS: the full pool (Tier 1 first, then Tier 2).
// Invariant: all ids are unique — duplicate ids would break the「重複は発生させない」
// 福引 contract (flow.md l.200). Enforced by the portraits.test.ts uniqueness assertion.
// ---------------------------------------------------------------------------
export const PORTRAITS: readonly Portrait[] = [...GATEKEEPERS, ...GENERICS];

// Module-level O(1) lookup map — built once, reused by portraitById().
// Mirror of collector.ts's pattern: stable lookups at module scope.
const PORTRAIT_BY_ID: ReadonlyMap<string, Portrait> = new Map(
  PORTRAITS.map((p) => [p.id, p]),
);

// ---------------------------------------------------------------------------
// Pure selectors — the seam downstream gacha/CPU-face holes will consume.
// Keep these names stable: gacha and opponent-face wiring will import them by name.
// ---------------------------------------------------------------------------

/** All 35 portraits (5 gatekeepers + 30 generics), stable order. */
export function allPortraits(): readonly Portrait[] {
  return PORTRAITS;
}

/** The 5 gatekeeper portraits (Tier 1, flow.md l.80–87). */
export function gatekeepers(): readonly Portrait[] {
  return GATEKEEPERS;
}

/** The 30 generic placeholder portraits (Tier 2). */
export function genericPortraits(): readonly Portrait[] {
  return GENERICS;
}

/** All portraits in a given 段位帯 (Tier 1 + Tier 2 for that band). */
export function portraitsByBand(band: DanBand): readonly Portrait[] {
  return PORTRAITS.filter((p) => p.band === band);
}

/** O(1) id lookup — the collection key used by the 福引 no-dup contract (flow.md l.200). */
export function portraitById(id: string): Portrait | null {
  return PORTRAIT_BY_ID.get(id) ?? null;
}

/** 昇段戦 → 門番 join (flow.md l.41): maps a transition name to its gatekeeper portrait.
 *  Returns null for unknown transitions (e.g. data-not-yet-populated future bands). */
export function gatekeeperForTransition(t: string): Portrait | null {
  return GATEKEEPERS.find((p) => p.transition === t) ?? null;
}
